/**
 * useWallPages — fetch/cache `GET /wall?q=&cursor=` pages, per plan d § Files. Takes the query the field has
 * settled on (`useSettledQuery`: the pause rule lives there, this hook waits for nothing), aborts the in-flight
 * request, stale-guards the response (a slow page for an abandoned query must never land after a faster one for the
 * current query: the wall shows the latest query only), and never blanks the wall while a new query is in flight — the
 * old tiles stay on screen until the new first page actually resolves (BRIEF § Instant search: "re-order never blanks
 * the wall").
 *
 * `seamAt` is the client's own tracking of the wire's `seam` flag (`contract.ts`'s `WallResponse`):
 * the boundary between the matched segment and the rest of the catalog, expressed as an index into
 * the accumulated `tiles` array. Amendment N13: an empty `q`'s `seam:true` is a routing signal only
 * — segment 2 starting cold — never a ghost trigger, so `seamAt` stays `null` whenever `q` is empty
 * regardless of what the wire says.
 *
 * `loadedQuery` is the query the tiles on screen actually answer, which is what `wall_search` must
 * report (review item S7) — during the debounce and the flight it still names the OLD query, and
 * only becomes `q` when that query's own page has landed.
 */
import {useCallback, useEffect, useRef, useState} from 'react'
import type {TileSummary, WallResponse} from '@core/wall/contract'

/** A page set this tab already had, handed back verbatim — see `wallReturn.ts`. */
export type RestoredPages = {
    query: string
    tiles: TileSummary[]
    nextCursor: string | null
    seamAt: number | null
    matchedCount: number | null
}

export type WallPagesState = {
    tiles: TileSummary[]
    /** Index into `tiles` where the ghost belongs — the seam of the current search — or `null`
     *  when no search is running, or the seam hasn't been reached by pages fetched so far. */
    seamAt: number | null
    matchedCount: number | null
    /** The query `tiles`/`seamAt`/`matchedCount` describe — not necessarily the `q` passed in. */
    loadedQuery: string
    loading: boolean
    hasMore: boolean
    /** The cursor the next page continues from, or `null` at the end of the catalog. */
    nextCursor: string | null
    loadMore: () => void
    restorePages: (restored: RestoredPages) => void
}

async function fetchWallPage(q: string, cursor: string | null, profile: string, signal: AbortSignal): Promise<WallResponse> {
    const params = new URLSearchParams()
    if (profile) params.set('p', profile)
    if (q) params.set('q', q)
    if (cursor) params.set('cursor', cursor)
    const res = await fetch(`/wall${params.toString() ? `?${params}` : ''}`, {signal})
    if (!res.ok) throw new Error(`GET /wall ${res.status}`)
    return res.json()
}

/**
 * The first page of a query, and whether it was read through the seam. A query that matches nothing answers an empty
 * page whose cursor leads to the rest of the wall (`seam: true`, `matched_count: 0`): reading that rest here, in the same
 * step, lets the wall keep what it shows until the whole answer can replace it, instead of standing blank under the ghost
 * for the second round trip. `seamAt` is then 0, the ghost first and the rest after it.
 */
export async function readFirstPage(
    query: string, profile: string, signal: AbortSignal, fetchPage: typeof fetchWallPage = fetchWallPage,
): Promise<{page: WallResponse; throughSeam: boolean}> {
    const first = await fetchPage(query, null, profile, signal)
    if (query.trim().length === 0 || !first.seam || first.tiles.length > 0 || !first.next_cursor) {
        return {page: first, throughSeam: false}
    }
    const rest = await fetchPage(query, first.next_cursor, profile, signal)
    return {page: {...rest, seam: true, matched_count: first.matched_count ?? 0}, throughSeam: true}
}

/** `profile`: the visitor's profile key (`profile.ts`), sent as `?p=` on every read; a page already fetched keeps the
 *  order it was read in, the next read (a new query, a reload) takes the key as it stands. `steerEpoch` is the count of
 *  the visitor's steering changes of that key: a new count reads the first page again, the way a new `q` does. */
export function useWallPages(q: string, initialPage?: WallResponse, profile = '', steerEpoch = 0): WallPagesState {
    const [tiles, setTiles] = useState<TileSummary[]>(initialPage?.tiles ?? [])
    const [seamAt, setSeamAt] = useState<number | null>(
        initialPage?.seam && q.trim().length > 0 ? initialPage.tiles.length : null,
    )
    const [matchedCount, setMatchedCount] = useState<number | null>(initialPage?.matched_count ?? null)
    const [nextCursor, setNextCursor] = useState<string | null>(initialPage?.next_cursor ?? null)
    const [loadedQuery, setLoadedQuery] = useState(q)
    const [loading, setLoading] = useState(false)

    // Guards against three race conditions with two refs: an in-flight fetch whose AbortController
    // didn't win the race (aborting is best-effort — Safari and slow proxies can still deliver an
    // aborted response), a loadMore() call issued twice before the first resolves, and a sentinel that
    // fires while a new query's first page is in flight: `loadMore` would take the next seq for a
    // cursor belonging to the previous query. `queryRef` is the second half of the guard: a page is
    // only accepted for the query currently asked.
    const requestSeq = useRef(0)
    const queryRef = useRef(q)
    const profileRef = useRef(profile)
    profileRef.current = profile
    const abortRef = useRef<AbortController | null>(null)
    const initialQRef = useRef(q)
    const skippedFirstEffect = useRef(!!initialPage)
    const tilesLenRef = useRef(tiles.length)
    useEffect(() => {
        tilesLenRef.current = tiles.length
    }, [tiles.length])

    const runFetch = useCallback(async (query: string, cursor: string | null, seq: number, append: boolean) => {
        abortRef.current?.abort()
        const controller = new AbortController()
        abortRef.current = controller
        setLoading(true)
        try {
            const read = append
                ? {page: await fetchWallPage(query, cursor, profileRef.current, controller.signal), throughSeam: false}
                : await readFirstPage(query, profileRef.current, controller.signal)
            const page = read.page
            // Superseded — a newer request already landed or is in flight, or the query moved on.
            if (requestSeq.current !== seq || queryRef.current !== query) return

            const priorLen = append ? tilesLenRef.current : 0
            const newLen = priorLen + page.tiles.length
            setTiles((prev) => (append ? [...prev, ...page.tiles] : page.tiles))
            tilesLenRef.current = newLen

            // The seam boundary sits AFTER every matched tile collected so far, including this
            // page's own — not before it. This page's `seam: true` means segment 1 (the matched
            // set) just exhausted, whether that happened on the very first page (priorLen 0) or
            // after several loadMore() pages of matches (priorLen > 0).
            if (read.throughSeam) setSeamAt(0)
            else if (page.seam && query.trim().length > 0) setSeamAt(newLen)
            else if (!append) setSeamAt(null)

            if (page.matched_count !== undefined) setMatchedCount(page.matched_count)
            else if (!append) setMatchedCount(null)
            setNextCursor(page.next_cursor)
            setLoadedQuery(query)
        } catch (err) {
            if ((err as Error)?.name !== 'AbortError') {
                // A failed wall fetch keeps showing whatever is already on screen — the wall never
                // blanks itself over a transient network error. Failure-logging review finding 8:
                // this branch used to be an `if` with nothing in it — logged now so a persistent
                // outage on the most-hit, least-instrumented route is at least visible in the
                // console; nothing on screen changes.
                console.error('[useWallPages] GET /wall failed', err)
            }
        } finally {
            if (requestSeq.current === seq) setLoading(false)
        }
    }, [])

    // q change → fresh fetch (segment reset). Skips the very first render when an
    // `initialPage` was handed in (the SSR first page) — that page is already the correct result
    // for the initial `q`, no redundant fetch on mount.
    useEffect(() => {
        if (skippedFirstEffect.current && q === initialQRef.current && steerEpoch === 0) {
            skippedFirstEffect.current = false
            queryRef.current = q
            return
        }
        skippedFirstEffect.current = false

        // The cursor belongs to the query that produced it: dropping it here both stops the
        // sentinel from paging the abandoned segment (`hasMore` goes false until the new first
        // page lands) and keeps the seam of the old query from being read against new tiles.
        queryRef.current = q
        setNextCursor(null)
        setSeamAt(null)

        const seq = ++requestSeq.current
        runFetch(q, null, seq, false)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- runFetch is stable (no deps)
    }, [q, steerEpoch])

    useEffect(() => () => abortRef.current?.abort(), [])

    const loadMore = useCallback(() => {
        if (loading || !nextCursor || q !== queryRef.current) return
        const seq = ++requestSeq.current
        runFetch(q, nextCursor, seq, true)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- runFetch is stable (no deps)
    }, [loading, nextCursor, q])

    /**
     * Put a page set back — the pages, the cursor they continue from, and the seam they were read
     * at (`wallReturn.ts`, a Back from a pack page). It takes the next request seq before anything
     * else, which is what keeps a page belonging to the mount's own first fetch — in flight, or
     * with an older seq already taken — from landing on top of
     * what was just restored.
     */
    const restorePages = useCallback((restored: RestoredPages) => {
        requestSeq.current += 1
        abortRef.current?.abort()
        queryRef.current = restored.query
        setTiles(restored.tiles)
        tilesLenRef.current = restored.tiles.length
        setSeamAt(restored.seamAt)
        setMatchedCount(restored.matchedCount)
        setNextCursor(restored.nextCursor)
        setLoadedQuery(restored.query)
        setLoading(false)
    }, [])

    return {
        tiles, seamAt, matchedCount, loadedQuery, loading,
        hasMore: nextCursor !== null, nextCursor, loadMore, restorePages,
    }
}
